import { useEffect, useRef, useState } from 'react';
import type { CityId } from '../types';
import { CITIES, CITY_IDS } from '../lib/cities';
import './TopBar.css';
import { LANGUAGES, useT } from '../i18n';

interface Props {
    city: CityId;
    query: string;
    onQuery: (q: string) => void;
    onCity: (c: CityId) => void;
    resultCount: number;
}

/**
 * A single floating capsule holding search and the city switcher.
 *
 * v2 spent roughly a third of the viewport above the map on a logo bar, a
 * redundant "Halal Places Near You" heading and a subtitle, and still had no
 * search box at all. Here the map starts at the top of the screen and one
 * control floats over it.
 */
export function TopBar({ city, query, onQuery, onCity, resultCount }: Props) {
    const { t, tn, lang, setLang } = useT();
    const [pickerOpen, setPickerOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const pickerRef = useRef<HTMLDivElement>(null);
    const langRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Close on Escape or an outside click, and return focus to the trigger.
    useEffect(() => {
        if (!pickerOpen) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPickerOpen(false); };
        const onClick = (e: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false);
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onClick);
        return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick); };
    }, [pickerOpen]);

    useEffect(() => {
        if (!langOpen) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setLangOpen(false); };
        const onClick = (e: MouseEvent) => {
            if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onClick);
        return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick); };
    }, [langOpen]);

    // "/" focuses search, the convention on map and search products.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === '/' && document.activeElement?.tagName !== 'INPUT') {
                e.preventDefault();
                inputRef.current?.focus();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);

    return (
        <div className="topbar">
            <div className="topbar__capsule">
                <label className="topbar__search">
                    <span className="visually-hidden">{t('search.label', { city: CITIES[city].name })}</span>
                    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
                        <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
                    </svg>
                    <input
                        ref={inputRef}
                        type="search"
                        value={query}
                        placeholder={t('search.placeholder', { city: CITIES[city].name })}
                        onChange={(e) => onQuery(e.target.value)}
                        autoComplete="off"
                        enterKeyHint="search"
                    />
                    {query && (
                        <button type="button" className="topbar__clear" onClick={() => { onQuery(''); inputRef.current?.focus(); }} aria-label={t('search.clear')}>
                            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                                <path d="M18 6 6 18M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </label>

                <div className="topbar__picker" ref={pickerRef}>
                    <button
                        type="button"
                        className="topbar__city"
                        onClick={() => setPickerOpen((o) => !o)}
                        aria-expanded={pickerOpen}
                        aria-haspopup="listbox"
                    >
                        <span aria-hidden="true">{CITIES[city].flag}</span>
                        <span className="topbar__city-name">{CITIES[city].name}</span>
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="m6 9 6 6 6-6" />
                        </svg>
                    </button>

                    {pickerOpen && (
                        <ul className="topbar__menu" role="listbox" aria-label={t('city.choose')}>
                            {CITY_IDS.map((id) => (
                                <li key={id} role="option" aria-selected={id === city}>
                                    <button type="button" onClick={() => { onCity(id); setPickerOpen(false); }} className={id === city ? 'is-current' : ''}>
                                        <span aria-hidden="true">{CITIES[id].flag}</span>
                                        <span className="topbar__menu-name">
                                            {CITIES[id].name}
                                            <span lang="zh">{CITIES[id].nameLocal !== CITIES[id].name ? CITIES[id].nameLocal : ''}</span>
                                        </span>
                                        <span className={`topbar__menu-trust trust-${CITIES[id].trust}`}>
                                            {t(`city.trust.${CITIES[id].trust}` as const)}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="topbar__picker" ref={langRef}>
                    <button
                        type="button"
                        className="topbar__lang"
                        onClick={() => setLangOpen((o) => !o)}
                        aria-expanded={langOpen}
                        aria-haspopup="listbox"
                        aria-label={t('lang.label')}
                    >
                        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                            <circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18" />
                        </svg>
                    </button>

                    {langOpen && (
                        <ul className="topbar__menu topbar__menu--lang" role="listbox" aria-label={t('lang.choose')}>
                            {LANGUAGES.map((l) => (
                                <li key={l.code} role="option" aria-selected={l.code === lang.code}>
                                    <button type="button" onClick={() => { setLang(l.code); setLangOpen(false); }}
                                            className={l.code === lang.code ? 'is-current' : ''} lang={l.code}>
                                        <span className="topbar__menu-name">
                                            {l.native}
                                            <span>{l.english}</span>
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {query && (
                <p className="topbar__hits" aria-live="polite">
                    {tn('search.matches_one', 'search.matches', resultCount)}
                </p>
            )}
        </div>
    );
}
